import { jsPDF } from 'jspdf';

class PdfService {
  /**
   * Generate a carbon reporting PDF for a farm and return a URL to open in a new tab
   * @param {Object} farmData - The farm data
   * @param {Array} woolLines - The wool lines associated with the farm
   * @returns {Promise<string>} - Promise resolving to the URL of the generated PDF
   */
  async generateCarbonReport(farmData = {}, woolLines = []) {
    // Log the farm data received by the PDF service
    console.log('PdfService: Received farm data:', JSON.stringify(farmData));
    console.log('PdfService: Received wool lines count:', woolLines.length);
    // Create a new PDF document
    const doc = new jsPDF();
    
    // Add Atralis logo from file
    // For this to work, you need to place the logo at:
    // /Users/robbie/agricore/agricore-frontend/public/images/atralis-logo.png
    
    try {
      await this.addLogoToDocument(doc);
    } catch (error) {
      console.error('Error adding logo:', error);
      // Fallback to text if image fails to load
      doc.setFontSize(20);
      doc.setTextColor(58, 137, 95); // #3A895F color
      doc.setFont('helvetica', 'bold');
      doc.text('ATRALIS', 20, 30);
    }
    
    // Add title with proper farm name on a new line below the logo
    doc.setFontSize(18);
    doc.setTextColor(60, 137, 95); // Green color
    // Ensure we use the farm name if available
    // Add null check for farmData
    console.log('PdfService: Available name fields:', {
      name: farmData?.name,
      FarmName: farmData?.FarmName,
      farm_name: farmData?.farm_name
    });
    const farmName = farmData?.name || farmData?.FarmName || farmData?.farm_name || 'Farm';
    console.log('PdfService: Using farm name:', farmName);
    doc.text(`Carbon Reporting: ${farmName}`, 20, 60);
    doc.setFont('helvetica', 'normal');
    
    // Add farm details - ensure we get data from all possible sources
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    console.log('PdfService: Available PIC fields:', {
      PIC: farmData?.PIC,
      pic: farmData?.pic
    });
    const pic = farmData?.PIC || farmData?.pic || 'N/A';
    console.log('PdfService: Using PIC:', pic);
    doc.text(`Property Identification Code (PIC): ${pic}`, 20, 75);
    
    // Try to get location from multiple possible fields
    console.log('PdfService: Available location fields:', {
      location: farmData?.location,
      Location: farmData?.Location,
      Region: farmData?.Region,
      LGA: farmData?.LGA,
      state: farmData?.state,
      region: farmData?.region,
      FarmLocation: farmData?.FarmLocation
    });
    const location = farmData?.location || 
                    farmData?.Location || 
                    (farmData?.Region && farmData?.LGA ? `${farmData.Region}, ${farmData.LGA}` : null) ||
                    (farmData?.state ? `${farmData.state}${farmData?.region ? ', ' + farmData.region : ''}` : null) ||
                    farmData?.FarmLocation || 
                    'N/A';
    console.log('PdfService: Using location:', location);
    doc.text(`Location: ${location}`, 20, 82);
    
    // Try to get owner from multiple possible fields
    console.log('PdfService: Available owner fields:', {
      owner: farmData?.owner,
      Owner: farmData?.Owner,
      OwnerName: farmData?.OwnerName,
      farm_owner: farmData?.farm_owner
    });
    const owner = farmData?.owner || 
                 farmData?.Owner || 
                 farmData?.OwnerName || 
                 farmData?.farm_owner || 
                 'N/A';
    console.log('PdfService: Using owner:', owner);
    doc.text(`Owner: ${owner}`, 20, 89);
    
    // Add carbon reporting section
    doc.setFontSize(16);
    doc.setTextColor(60, 137, 95);
    doc.text('Carbon Reporting Status', 20, 105);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    // Get carbon reporting status from all possible field names
    console.log('PdfService: Available carbon reporting status fields:', {
      CarbonReportingStatus: farmData?.CarbonReportingStatus,
      carbon_reporting_status: farmData?.carbon_reporting_status,
      carbonReportingStatus: farmData?.carbonReportingStatus,
      GHGScopesCovered: farmData?.GHGScopesCovered
    });
    
    let carbonStatus;
    if (farmData?.GHGScopesCovered) {
      carbonStatus = `${farmData.GHGScopesCovered} (${farmData.CarbonReportingStatus || 'Estimated'})`;
    } else {
      carbonStatus = farmData?.CarbonReportingStatus || 
                   farmData?.carbon_reporting_status || 
                   farmData?.carbonReportingStatus || 
                   'Not Available';
    }
    
    console.log('PdfService: Using carbon status:', carbonStatus);
    doc.text(`Carbon Reporting Status: ${carbonStatus}`, 20, 115);
    doc.text(`Scope 3 Compliant: ${farmData?.Scope3_Compliant || farmData?.scope3_compliant ? 'Yes' : 'No'}`, 20, 122);
    
    // Add sustainability practices
    doc.setFontSize(16);
    doc.setTextColor(60, 137, 95);
    doc.text('Sustainability Practices', 20, 135);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    doc.text(`Certification: ${farmData?.certification || farmData?.Certification || 'Standard'}`, 20, 145);
    
    // Add sustainability practices bullet points
    const practices = [
      'Rotational grazing to improve soil carbon sequestration',
      'Native vegetation corridors for biodiversity',
      'Solar-powered water pumps and farm infrastructure',
      'Reduced chemical inputs through integrated pest management',
      'Water conservation through improved irrigation efficiency'
    ];
    
    let yPosition = 155;
    practices.forEach(practice => {
      doc.text(`\u2022 ${practice}`, 25, yPosition);
      yPosition += 7;
    });
    
    // Add wool lines section
    doc.setFontSize(16);
    doc.setTextColor(60, 137, 95);
    doc.text('Associated Wool Lines', 20, 195);
    
    doc.setFontSize(12);
    doc.setTextColor(0, 0, 0);
    
    // Process wool lines to ensure we have data
    const processedWoolLines = woolLines && woolLines.length > 0 ? 
      woolLines.map(line => {
        // Try to extract data from all possible field names
        return {
          lineId: line.lineId || line.LineID || line.line_id || 'N/A',
          micron: line.micron || line.Micron || line.Micron_um || line.micron_um || 'N/A',
          yield: line.yield || line.Yield || line.Yield_pct || line.yield_pct || 'N/A'
        };
      }) : [];
    
    if (processedWoolLines.length > 0) {
      // Create table header
      doc.text('Line ID', 25, 205);
      doc.text('Micron', 75, 205);
      doc.text('Yield', 115, 205);
      doc.text('Carbon Footprint', 155, 205);
      
      // Add line
      doc.setLineWidth(0.1);
      doc.line(20, 208, 190, 208);
      
      // Add wool lines
      yPosition = 215;
      processedWoolLines.forEach(line => {
        doc.text(line.lineId.toString(), 25, yPosition);
        doc.text(`${line.micron.toString()} \u00b5m`, 75, yPosition);
        doc.text(`${line.yield.toString()}%`, 115, yPosition);
        
        // Calculate mock carbon footprint (kg CO2e per kg wool)
        const carbonFootprint = (Math.random() * 15 + 20).toFixed(1);
        doc.text(`${carbonFootprint} kg CO2e/kg`, 155, yPosition);
        
        yPosition += 8;
      });
    } else {
      doc.text('No wool lines associated with this farm.', 25, 205);
    }
    
    // Add footer with Atralis Platform
    doc.setFontSize(10);
    doc.setTextColor(100, 100, 100);
    const today = new Date().toISOString().split('T')[0];
    doc.text(`Generated on ${today} using the Atralis Platform`, 20, 280);
    
    // Generate the PDF as a blob
    const pdfBlob = doc.output('blob');
    
    // Create a URL for the blob
    const blobUrl = URL.createObjectURL(pdfBlob);
    
    // Return the blob URL
    return blobUrl;
  }

  /**
   * Add the Atralis logo to the PDF document
   * @param {jsPDF} doc - The PDF document
   * @returns {Promise<void>}
   */
  addLogoToDocument(doc) {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = function() {
        try {
          // Calculate aspect ratio to maintain proportions
          const aspectRatio = img.width / img.height;
          const width = 60;
          const height = width / aspectRatio;
          
          // Add the image to the PDF
          doc.addImage(img, 'PNG', 20, 15, width, height);
          resolve();
        } catch (error) {
          reject(error);
        }
      };
      img.onerror = function() {
        reject(new Error('Failed to load logo image'));
      };
      // Set the source to the logo file
      const baseUrl = window.location.origin;
      img.src = `${baseUrl}/images/atralis-logo.png`;
    });
  }
}

export default new PdfService();
